/**
 * ============================================================
 * NO HYPE HOCKEY — HORIZON STANDARD ACTION OVERRIDES
 * Version: 1.4
 * Theme: Horizon 4.1.5
 *
 * PURPOSE
 * - Preserve Horizon's standard Shopify cart actions
 * - Keep cart drawer continuously open while:
 *      • removing an item
 *      • increasing quantity
 *      • decreasing quantity
 * - Close cart intentionally when shopper:
 *      • clicks X
 *      • clicks backdrop
 *      • presses Escape
 *      • clicks Continue shopping in empty drawer
 *
 * DRAWER CONTINUE SHOPPING BEHAVIOR
 * - Close the drawer
 * - Stay on the CURRENT page
 * - Do NOT redirect to No Hype Gear
 *
 * IMPORTANT
 * This applies only to the SIDE CART DRAWER.
 *
 * The full custom cart page can continue using its own
 * Continue Shopping link to No Hype Gear.
 * ============================================================
 */


/* ============================================================
   CONSTANTS
   ============================================================ */

const NHH_CART_DRAWER_SELECTOR =
  'theme-drawer#cart-drawer';

const NHH_CART_ITEMS_SELECTOR =
  `${NHH_CART_DRAWER_SELECTOR} cart-items-component[data-drawer]`;


/* ============================================================
   CART DRAWER HELPER
   ============================================================ */

function getCartDrawer() {

  return document.querySelector(
    NHH_CART_DRAWER_SELECTOR
  );

}


/* ============================================================
   CART DRAWER BEHAVIOR
   ============================================================ */

/**
 * Horizon processes its declarative cart actions separately.
 *
 * For cart mutations:
 * - allow Horizon to perform the cart update
 * - prevent the click from bubbling into drawer backdrop logic
 *
 * For drawer Continue Shopping:
 * - prevent navigation
 * - close the drawer only
 * - leave shopper on the current page
 */
function bindCartDrawerBehavior() {

  const cartItems =
    document.querySelector(
      NHH_CART_ITEMS_SELECTOR
    );


  if (
    !cartItems ||
    cartItems.dataset.nhhDrawerBehavior === 'true'
  ) {

    return;

  }


  cartItems.dataset.nhhDrawerBehavior =
    'true';


  cartItems.addEventListener(
    'click',
    async function (event) {

      const target =
        event.target instanceof Element
          ? event.target
          : null;


      if (!target) {
        return;
      }


      /* ======================================================
         EMPTY CART — CONTINUE SHOPPING

         Side cart behavior:
         CLOSE DRAWER + STAY ON CURRENT PAGE
         ====================================================== */

      const continueShopping =
        target.closest(
          '.cart-items__empty-button'
        );


      if (continueShopping) {

        /*
         * Prevent Horizon / browser navigation.
         *
         * We intentionally ignore the link destination here.
         * In the side drawer, Continue Shopping means:
         * "close this drawer and return to what I was doing."
         */

        event.preventDefault();

        event.stopPropagation();


        const drawer =
          getCartDrawer();


        if (
          drawer &&
          typeof drawer.close === 'function'
        ) {

          try {

            await drawer.close();

          } catch (error) {

            console.warn(
              'NHH Cart Drawer: drawer close encountered an issue.',
              error
            );

          }

        }


        return;

      }


      /* ======================================================
         REMOVE ITEM

         Allow Horizon to process the remove action.
         Prevent that same click from bubbling into the drawer
         backdrop logic.
         ====================================================== */

      const removeButton =
        target.closest(
          '.cart-items__remove'
        );


      if (removeButton) {

        event.stopPropagation();

        return;

      }


      /* ======================================================
         QUANTITY + / -

         Same behavior:
         update cart but keep drawer continuously open.
         ====================================================== */

      const quantityButton =
        target.closest(
          'cart-quantity-selector-component button'
        );


      if (quantityButton) {

        event.stopPropagation();

        return;

      }

    },
    false
  );

}


/* ============================================================
   CART CONTENT OBSERVER

   Horizon may morph or replace cart content after an AJAX
   update. Re-bind our behavior if the cart-items component
   changes.

   This observer does NOT open or close the drawer.
   ============================================================ */

const nhhCartObserver =
  new MutationObserver(
    function () {

      bindCartDrawerBehavior();

    }
  );


function startCartObserver() {

  if (!document.body) {
    return;
  }


  nhhCartObserver.observe(
    document.body,
    {
      childList:
        true,

      subtree:
        true
    }
  );

}


/* ============================================================
   SHOPIFY STANDARD ACTIONS
   ============================================================ */

function initShopifyActions() {

  const actions =
    window.Shopify?.actions;


  if (!actions) {
    return;
  }


  /* ==========================================================
     UPDATE CART
     ========================================================== */

  actions.updateCart.configure(
    {
      eventTarget:
        () =>
          getCartDrawer() ??
          document
    }
  );


  /* ==========================================================
     OPEN CART
     ========================================================== */

  actions.openCart.configure(
    {
      async handler() {

        const drawer =
          getCartDrawer();


        if (
          drawer &&
          typeof drawer.open === 'function'
        ) {

          drawer.open();

          return;

        }


        const cartUrl =
          window.Theme?.routes?.cart_url ||
          '/cart';


        window.location.href =
          cartUrl;

      }
    }
  );

}


/* ============================================================
   INITIALIZATION
   ============================================================ */

function init() {

  initShopifyActions();

  bindCartDrawerBehavior();

  startCartObserver();

}


/* ============================================================
   INITIAL PAGE LOAD
   ============================================================ */

if (
  document.readyState === 'loading'
) {

  document.addEventListener(
    'DOMContentLoaded',
    init,
    {
      once:
        true
    }
  );

} else {

  init();

}


/* ============================================================
   SHOPIFY THEME EDITOR
   ============================================================ */

document.addEventListener(
  'shopify:section:load',
  function () {

    bindCartDrawerBehavior();

  }
);